/**
 * @module modules/plot-summary-sk
 * @description <h2><code>plot-summary-sk</code></h2>
 *
 * @evt summary_selected - Event produced when the user selects a section on the summary.
 *
 * @attr
 *
 * @example
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "PlotSummarySk", {
    enumerable: true,
    get: function() {
        return PlotSummarySk;
    }
});
require("@google-web-components/google-chart");
require("@material/web/iconbutton/icon-button");
const _dataframe_context = require("../dataframe/dataframe_context");
const _context = require("@lit/context");
const _lit = require("lit");
const _ref = require("lit/directives/ref.js");
const _decorators = require("lit/decorators.js");
const _when = require("lit/directives/when.js");
const _plotbuilder = require("../common/plot-builder");
const _index = require("../dataframe/index");
const _define = require("../../../elements-sk/modules/define");
const _telemetry = require("../telemetry/telemetry");
const _plotsummaryskcss = require("./plot-summary-sk.css");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
const dayInSeconds = 60 * 60 * 24;
let PlotSummarySk = class PlotSummarySk extends _lit.LitElement {
    updated(changedProperties) {
        if (changedProperties.has('data') || changedProperties.has('selectedTrace') || changedProperties.has('domain')) {
            this.updateDataView(this.data, this.selectedTrace);
        }
    }
    updateDataView(dt, trace) {
        const start = performance.now();
        const plot = this.plotElement.value;
        if (!plot || !dt) {
            if (dt) {
                console.warn('The dataframe is not assigned because the element is not ready. Try call `await this.updateComplete` first.');
            }
            return;
        }
        let view;
        // Downsample the data using Min-Max bucketing if there are too many points.
        // This preserves visual spikes by plotting the range (min and max) for each bucket.
        const numRows = dt.getNumberOfRows();
        const maxPoints = 1000; // Target resolution (approximate pairs of min/max)
        if (numRows > maxPoints) {
            // We want ~maxPoints visualization points. Since we generate 2 rows per bucket,
            // we need maxPoints / 2 buckets.
            const bucketSize = Math.ceil(2 * numRows / maxPoints);
            const newData = new google.visualization.DataTable();
            // 1. Copy Column Structure
            const numCols = dt.getNumberOfColumns();
            for(let i = 0; i < numCols; i++){
                newData.addColumn(dt.getColumnType(i), dt.getColumnLabel(i));
            }
            // 2. Aggregate Data into Buckets
            for(let i = 0; i < numRows; i += bucketSize){
                const bucketEndIndex = Math.min(i + bucketSize, numRows);
                // Initialize Row Structures
                // We'll have two rows: one for Min values, one for Max values.
                // For the Domain columns (0 and 1: Commit/Date), we use start and end times.
                const minRow = new Array(numCols);
                const maxRow = new Array(numCols);
                // Set Domain (Time/Commit) values
                // Row 1 uses the Start of the bucket
                minRow[0] = dt.getValue(i, 0);
                minRow[1] = dt.getValue(i, 1);
                // Row 2 uses the End of the bucket (last element)
                // This spreads the visual "ink" across the time interval.
                const lastIdx = bucketEndIndex - 1;
                maxRow[0] = dt.getValue(lastIdx, 0);
                maxRow[1] = dt.getValue(lastIdx, 1);
                // Initialize Data columns (2+) with null values for comparison
                for(let col = 2; col < numCols; col++){
                    minRow[col] = null;
                    maxRow[col] = null;
                }
                // 3. Scan the bucket to find Min/Max for each column
                let hasData = false;
                for(let r = i; r < bucketEndIndex; r++){
                    for(let col = 2; col < numCols; col++){
                        const val = dt.getValue(r, col);
                        if (typeof val === 'number') {
                            if (minRow[col] === null || val < minRow[col]) minRow[col] = val;
                            if (maxRow[col] === null || val > maxRow[col]) maxRow[col] = val;
                            hasData = true;
                        }
                    }
                }
                // 4. Add the synthesized rows
                if (hasData) {
                    newData.addRow(minRow);
                    newData.addRow(maxRow);
                }
            }
            view = new google.visualization.DataView(newData);
        } else {
            view = new google.visualization.DataView(dt);
        }
        const options = (0, _plotbuilder.SummaryChartOptions)(getComputedStyle(this), this.domain);
        options.colors = [];
        // The first two columns are the commit position and the date.
        const cols = [
            this.domain === 'commit' ? 0 : 1
        ];
        for(let i = 2; i < dt.getNumberOfColumns(); i++){
            const traceKey = dt.getColumnLabel(i);
            // Assign a specific color to all labels if not already present.
            if (!this.traceColorMap.has(traceKey)) {
                this.traceColorMap.set(traceKey, (0, _plotbuilder.getTraceColor)(traceKey));
            }
            if (!trace || trace === traceKey) {
                cols.push(i);
                options.colors.push(this.traceColorMap.get(traceKey));
            }
        }
        view.setColumns(cols);
        plot.view = view;
        plot.options = options;
        _telemetry.telemetry.recordSummary(_telemetry.SummaryMetric.GoogleGraphPlotTime, (performance.now() - start) / 1000, {
            type: 'summary'
        });
    }
    // The current selected value saved for re-adjusting selection box.
    // The google chart reloads and redraws themselves asynchronously, we need to
    // save the selection range and apply it after we received the `ready` event.
    // private cachedSelectedValueRange: range | null = null; // Replaced by this.selection (keeping usage commented out for incremental refactor steps if needed, but actually I will just remove it now to avoid confusion)
    controlTemplate(side) {
        const chunk = this.loadingChunk;
        const direction = side === 'left' ? -1 : 1;
        const onClickLoad = async ({ target })=>{
            const btn = target;
            // Check if multichart is enabled.
            const isMultiChart = document.querySelectorAll('explore-simple-sk').length > 1 ? true : false;
            if (isMultiChart) {
                var _this_dfRepo;
                var _this_dfRepo_commitRange;
                // If the multi-chart is enabled, we need to update the selection in all charts.
                // When extending the range, we need to update other charts to match.
                const detail = {
                    value: (_this_dfRepo_commitRange = (_this_dfRepo = this.dfRepo) === null || _this_dfRepo === void 0 ? void 0 : _this_dfRepo.commitRange) !== null && _this_dfRepo_commitRange !== void 0 ? _this_dfRepo_commitRange : (0, _index.range)(0, 0),
                    domain: this.domain,
                    offsetInSeconds: chunk * direction
                };
                this.dispatchEvent(new CustomEvent('range-changing-in-multi', {
                    bubbles: true,
                    detail: detail
                }));
            } else {
                var _this_dfRepo1;
                await ((_this_dfRepo1 = this.dfRepo) === null || _this_dfRepo1 === void 0 ? void 0 : _this_dfRepo1.extendRange(chunk * direction, false));
            }
            btn.selected = false;
        };
        return (0, _lit.html)`
      ${(0, _when.when)(this.hasControl && this.dfRepo, ()=>(0, _lit.html)` <md-icon-button
            toggle
            ?disabled="${this.loading}"
            class="load-btn"
            @click=${onClickLoad}>
            <md-icon>keyboard_double_arrow_${side}</md-icon>
            <div slot="selected" class="loader"></div>
          </md-icon-button>`)}
    `;
    }
    render() {
        return (0, _lit.html)`
      ${this.controlTemplate('left')}
      <div class="container hover-to-show-link">
        <google-chart
          ${(0, _ref.ref)(this.plotElement)}
          class="plot"
          type="area"
          @google-chart-ready=${this.onGoogleChartReady}>
        </google-chart>
        <h-resizable-box-sk
          ${(0, _ref.ref)(this.selectionBox)}
          .selectionRange=${this.selectionCoords}
          @selection-changed=${this.onSelectionChanged}></h-resizable-box-sk>
        ${(0, _when.when)(this.loading, ()=>(0, _lit.html)`<div class="overlay">
              <div class="loader"></div>
            </div>`)}
      </div>
      ${this.controlTemplate('right')}
    `;
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('domain')) {
            this.domainChanged = true;
        }
    }
    connectedCallback() {
        super.connectedCallback();
        const resizeObserver = new ResizeObserver((entries)=>{
            entries.forEach(()=>{
                var // The google chart needs to redraw when it is resized.
                _this_plotElement_value;
                (_this_plotElement_value = this.plotElement.value) === null || _this_plotElement_value === void 0 ? void 0 : _this_plotElement_value.redraw();
                this.requestUpdate();
            });
        });
        resizeObserver.observe(this);
    }
    onGoogleChartReady() {
        if (this.domainChanged) {
            this.domainChanged = false;
            if (this.selectionCoords) {
                const newValue = this.convertToValueRange(this.selectionCoords, this.domain);
                if (newValue) {
                    this.selection = newValue;
                    this.dispatchEvent(new CustomEvent('summary_selected', {
                        detail: {
                            start: 0,
                            end: 0,
                            value: newValue,
                            domain: this.domain
                        },
                        bubbles: true
                    }));
                    return;
                }
            }
        }
        this.updateSelectionCoords();
    }
    onSelectionChanged(e) {
        // The h-resizable-box-sk emits the NEW coords range.
        const coordsRange = e.detail;
        this.selectionCoords = coordsRange; // Optimistic update
        // Convert back to value for external consumers
        const valueRange = this.convertToValueRange(coordsRange, this.domain) || (0, _index.range)(0, 0);
        this.selection = valueRange; // Source of truth update
        this.dispatchEvent(new CustomEvent('summary_selected', {
            detail: {
                start: 0,
                end: 0,
                value: valueRange,
                domain: this.domain
            },
            bubbles: true
        }));
    }
    // Helper to sync coords from value
    updateSelectionCoords() {
        if (this.selection) {
            const coords = this.convertToCoordsRange(this.selection, this.domain);
            if (coords) {
                this.selectionCoords = coords;
            }
        } else {
            this.selectionCoords = null;
        }
    }
    // Converts from the value range to the coordinates range.
    convertToCoordsRange(valueRange, domain) {
        var _chart, _chart1;
        const chart = this.chartLayout;
        if (!chart || !valueRange) {
            return null;
        }
        const isCommitScale = domain === 'commit';
        const startX = (_chart = chart) === null || _chart === void 0 ? void 0 : _chart.getXLocation(isCommitScale ? valueRange.begin : new Date(valueRange.begin * 1000));
        const endX = (_chart1 = chart) === null || _chart1 === void 0 ? void 0 : _chart1.getXLocation(isCommitScale ? valueRange.end : new Date(valueRange.end * 1000));
        return (0, _index.range)(startX, endX);
    }
    // Converts from the value range to the coordinates range.
    convertToValueRange(coordsRange, domain) {
        const chart = this.chartLayout;
        if (!chart || !coordsRange) {
            return null;
        }
        const range = {
            begin: chart.getHAxisValue(coordsRange.begin),
            end: chart.getHAxisValue(coordsRange.end)
        };
        // The date is saved in Date, we need to convert to the UNIX timestamp.
        if (domain === 'date') {
            range.begin = range.begin.getTime() / 1000;
            range.end = range.end.getTime() / 1000;
        }
        return range;
    }
    // Get the current selected value range.
    get selectedValueRange() {
        if (this.selection) {
            return this.selection;
        }
        // If no selection state, we shouldn't rely on the box's value or chart layout directly
        // because we should be driven by state.
        return {
            begin: 0,
            end: 0
        };
    }
    // Set the current selected value range.
    set selectedValueRange(range) {
        this.selection = range;
        this.updateSelectionCoords();
    }
    // SelectRange sets the selection range on the plot summary bar.
    SelectRange(selectionRange) {
        this.selectedValueRange = selectionRange;
    }
    // Select the provided range on the plot-summary.
    Select(begin, end) {
        const isCommitScale = this.domain === 'commit';
        const col = isCommitScale ? 'offset' : 'timestamp';
        this.SelectRange({
            begin: begin[col],
            end: end[col]
        });
    }
    // Get the underlying ChartLayoutInterface.
    // This provides API to inspect the traces and coordinates.
    get chartLayout() {
        const gchart = this.plotElement.value;
        if (!gchart) {
            return null;
        }
        const wrapper = gchart['chartWrapper'];
        if (!wrapper) {
            return null;
        }
        const chart = wrapper.getChart();
        return chart && chart.getChartLayoutInterface();
    }
    // Add all the event listeners.
    addEventListeners() {
        // If the user toggles the theme to/from darkmode then redraw.
        document.addEventListener('theme-chooser-toggle', ()=>{
            // Update the options to trigger the redraw.
            if (this.plotElement.value && this.data) {
                this.plotElement.value.options = (0, _plotbuilder.SummaryChartOptions)(getComputedStyle(this), this.domain);
            }
            this.requestUpdate();
        });
    }
    get _viewForTesting() {
        var _this_plotElement_value;
        return ((_this_plotElement_value = this.plotElement.value) === null || _this_plotElement_value === void 0 ? void 0 : _this_plotElement_value.view) || null;
    }
    constructor(){
        super();
        this.loading = false;
        this.domain = 'commit';
        this.data = null;
        this.selectedTrace = null;
        // load ~1Q worth of data as developer cycles tend to
        // revolve around quarterly milestones. Users are likely
        // to want to see historical data in this increment.
        this.loadingChunk = 90 * dayInSeconds;
        this.hasControl = false;
        this.selection = null;
        // Maps a trace to a color.
        this.traceColorMap = new Map();
        // The div element that will host the plot on the summary.
        this.plotElement = (0, _ref.createRef)();
        this.selectionCoords = null;
        // The resizable selection box to draw the selection.
        this.selectionBox = (0, _ref.createRef)();
        this.domainChanged = false;
        this.addEventListeners();
    }
};
(()=>{
    PlotSummarySk.styles = _plotsummaryskcss.style;
})();
_ts_decorate([
    (0, _context.consume)({
        context: _dataframe_context.dataframeRepoContext
    }),
    _ts_metadata("design:type", typeof _dataframe_context.DataFrameRepository === "undefined" ? Object : _dataframe_context.DataFrameRepository)
], PlotSummarySk.prototype, "dfRepo", void 0);
_ts_decorate([
    (0, _context.consume)({
        context: _dataframe_context.dataframeLoadingContext,
        subscribe: true
    }),
    (0, _decorators.property)({
        reflect: true,
        type: Boolean
    })
], PlotSummarySk.prototype, "loading", void 0);
_ts_decorate([
    (0, _decorators.property)({
        reflect: true
    }),
    _ts_metadata("design:type", String)
], PlotSummarySk.prototype, "domain", void 0);
_ts_decorate([
    (0, _context.consume)({
        context: _dataframe_context.dataTableContext,
        subscribe: true
    }),
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", typeof _dataframe_context.DataTable === "undefined" ? Object : _dataframe_context.DataTable)
], PlotSummarySk.prototype, "data", void 0);
_ts_decorate([
    (0, _decorators.property)({
        attribute: true
    }),
    _ts_metadata("design:type", Object)
], PlotSummarySk.prototype, "selectedTrace", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Number
    })
], PlotSummarySk.prototype, "loadingChunk", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean
    }),
    _ts_metadata("design:type", Boolean)
], PlotSummarySk.prototype, "hasControl", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Object)
], PlotSummarySk.prototype, "selection", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Object)
], PlotSummarySk.prototype, "selectionCoords", void 0);
(0, _define.define)('plot-summary-sk', PlotSummarySk);
